<template>
  <a-page
    :id="app_id"
    :image_source="image_source"
    :image_height="image_height"
  >
    <v-container>
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              <v-icon left>mdi-file-check-outline</v-icon>
              {{ $t('reservations_validator_title') }}
            </v-card-title>
            <v-card-text>
              <v-row>
                <!-- Selector d'arxiu -->
                <v-col cols="12" v-if="!loadedFromServer">
                  <v-file-input
                    v-model="file"
                    accept=".xlsx,.csv,.xls"
                    :label="$t('reservations_validator_select_file')"
                    prepend-icon="mdi-file-upload"
                    show-size
                    outlined
                    dense
                    @change="handleFileUpload"
                    clearable
                  ></v-file-input>
                </v-col>
                
                <!-- Indicador de càrrega des del servidor -->
                <v-col cols="12" v-if="loadedFromServer">
                  <v-alert type="info" dense outlined>
                    <v-icon left>mdi-cloud-download</v-icon>
                    {{ $t('reservations_validator_loaded_from_server') }}
                  </v-alert>
                </v-col>

                <!-- Informació de l'arxiu carregat -->
                <v-col cols="12" v-if="fileData">
                  <v-alert type="success" dense outlined>
                    <div>
                      <strong>{{ $t('reservations_validator_file_loaded') }}:</strong> {{ $t('reservations_validator_reservations_detected', { count: fileData.length }) }}
                    </div>
                    <div v-if="minCheckInDate && maxCheckOutDate" class="mt-1">
                      <v-icon small class="mr-1">mdi-calendar-range</v-icon>
                      {{ $t('reservations_validator_date_range', { from: formatDate(minCheckInDate), to: formatDate(maxCheckOutDate) }) }}
                    </div>
                  </v-alert>
                </v-col>

                <!-- Selecció de columnes -->
                <v-col cols="12" md="6" v-if="fileData && headers.length > 0">
                  <v-select
                    v-model="checkInColumn"
                    :items="headers"
                    :label="$t('reservations_validator_checkin_column')"
                    outlined
                    dense
                    prepend-icon="mdi-calendar-import"
                  ></v-select>
                </v-col>

                <v-col cols="12" md="6" v-if="fileData && headers.length > 0">
                  <v-select
                    v-model="checkOutColumn"
                    :items="headers"
                    :label="$t('reservations_validator_checkout_column')"
                    outlined
                    dense
                    prepend-icon="mdi-calendar-export"
                  ></v-select>
                </v-col>

                <v-col cols="12" md="6" v-if="fileData && headers.length > 0">
                  <v-select
                    v-model="statusColumn"
                    :items="headers"
                    :label="$t('reservations_validator_status_column')"
                    outlined
                    dense
                    clearable
                    prepend-icon="mdi-tag"
                    :hint="$t('reservations_validator_status_hint')"
                    persistent-hint
                  ></v-select>
                </v-col>

                <!-- Format de data -->
                <v-col cols="12" md="6" v-if="fileData">
                  <v-text-field
                    v-model="dateFormat"
                    :label="$t('reservations_validator_date_format')"
                    outlined
                    dense
                    prepend-icon="mdi-calendar-clock"
                    :hint="$t('reservations_validator_format_hint', { format: detectedDateFormat })"
                    persistent-hint
                  ></v-text-field>
                </v-col>

                <!-- Selector de data inicial -->
                <v-col cols="12" md="6" v-if="fileData">
                  <v-menu
                    v-model="datePickerMenuStart"
                    :close-on-content-click="false"
                    max-width="290"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="friendlyValidationDateStart"
                        :label="$t('reservations_validator_start_date')"
                        prepend-icon="mdi-calendar-start"
                        readonly
                        outlined
                        dense
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="validationDateStart"
                      @change="datePickerMenuStart = false"
                      first-day-of-week="1"
                      :max="validationDateEnd"
                    ></v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Selector de data final -->
                <v-col cols="12" md="6" v-if="fileData">
                  <v-menu
                    v-model="datePickerMenuEnd"
                    :close-on-content-click="false"
                    max-width="290"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="friendlyValidationDateEnd"
                        :label="$t('reservations_validator_end_date')"
                        prepend-icon="mdi-calendar-end"
                        readonly
                        outlined
                        dense
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="validationDateEnd"
                      @change="datePickerMenuEnd = false"
                      first-day-of-week="1"
                      :min="validationDateStart"
                    ></v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Botó de validació -->
                <v-col cols="12" class="text-center" v-if="canValidate">
                  <v-btn
                    color="primary"
                    large
                    rounded
                    @click="validateReservations"
                    :loading="validating"
                    :disabled="validating"
                  >
                    <v-icon left>mdi-check-circle</v-icon>
                    {{ $t('reservations_validator_validate_button') }}
                  </v-btn>
                  
                  <div v-if="validating" class="mt-3">
                    <v-progress-linear
                      indeterminate
                      color="primary"
                      class="mb-2"
                    ></v-progress-linear>
                    <div class="text-caption grey--text">
                      {{ $t('reservations_validator_processing') }}
                    </div>
                  </div>
                  
                  <v-alert
                    v-if="dateRangeWarning && !validating"
                    type="info"
                    dense
                    outlined
                    class="mt-3"
                  >
                    {{ dateRangeWarning }}
                  </v-alert>
                </v-col>

                <!-- Arxiu de validació opcional -->
                <v-col cols="12" v-if="validationResults.length > 0">
                  <v-card outlined v-if="!validationFileData">
                    <v-card-title>
                      <v-icon left>mdi-file-compare</v-icon>
                      {{ $t('reservations_validator_validation_optional') }}
                    </v-card-title>
                    <v-card-text>
                      <v-file-input
                        v-model="validationFile"
                        accept=".xlsx,.csv,.xls"
                        :label="$t('reservations_validator_upload_validation_file')"
                        prepend-icon="mdi-file-excel"
                        show-size
                        outlined
                        dense
                        @change="handleValidationFileUpload"
                        clearable
                        :hint="$t('reservations_validator_validation_hint')"
                        persistent-hint
                      ></v-file-input>
                    </v-card-text>
                  </v-card>
                  <v-alert
                    v-else
                    type="info"
                    dense
                    dismissible
                    @input="clearValidationFile"
                  >
                    <v-icon left>mdi-file-check</v-icon>
                    {{ $t('reservations_validator_validation_loaded') }}
                  </v-alert>
                </v-col>

                <!-- Resultats -->
                <v-col cols="12" v-if="validationResults.length > 0">
                  <v-card outlined>
                    <v-card-title class="text-h5">
                      <v-icon left color="success" large>mdi-check-circle</v-icon>
                      {{ $t('reservations_validator_result_title') }}
                      <v-spacer></v-spacer>
                      <v-chip 
                        v-if="validationFileData" 
                        color="success" 
                        outlined
                      >
                        <v-icon left small>mdi-check-decagram</v-icon>
                        {{ $t('reservations_validator_validated_with_excel') }}
                      </v-chip>
                    </v-card-title>
                    <v-card-text>
                      <!-- Resum del període -->
                      <v-row class="mb-4">
                        <v-col cols="12" md="4">
                          <div class="text-h6 grey--text text--darken-1">
                            {{ $t('reservations_validator_period_analyzed') }}
                          </div>
                          <div class="text-subtitle-1">
                            {{ friendlyValidationDateStart }} - {{ friendlyValidationDateEnd }}
                          </div>
                          <v-chip color="primary" outlined small class="mt-2">
                            {{ $t('reservations_validator_days_analyzed', { count: validationResults.length }) }}
                          </v-chip>
                        </v-col>
                        <v-col cols="12" md="4" v-if="totalStats">
                          <div class="text-h6 grey--text text--darken-1">
                            {{ $t('reservations_validator_average_occupancy') }}
                          </div>
                          <div class="text-h4 primary--text">
                            {{ totalStats.average }} {{ $t('reservations_validator_reservations_per_day') }}
                          </div>
                          <div class="text-caption">
                            {{ $t('reservations_validator_max_min', { max: totalStats.max, min: totalStats.min }) }}
                          </div>
                        </v-col>
                        <v-col cols="12" md="4" v-if="validationSummary">
                          <div class="text-h6 grey--text text--darken-1">
                            {{ $t('reservations_validator_validation_summary') }}
                          </div>
                          <div class="mt-2">
                            <v-chip color="success" small class="mr-1">
                              <v-icon left x-small>mdi-check</v-icon>
                              {{ $t('reservations_validator_correct', { count: validationSummary.matched }) }}
                            </v-chip>
                            <v-chip v-if="validationSummary.mismatched > 0" color="error" small>
                              <v-icon left x-small>mdi-alert</v-icon>
                              {{ $t('reservations_validator_differences', { count: validationSummary.mismatched }) }}
                            </v-chip>
                          </div>
                        </v-col>
                      </v-row>

                      <!-- Alerta si hi ha diferències o èxit -->
                      <v-alert
                        v-if="validationSummary && validationSummary.mismatched > 0"
                        type="warning"
                        dense
                        outlined
                        class="mb-4"
                      >
                        <v-icon left>mdi-alert</v-icon>
                        {{ $t('reservations_validator_differences_alert', { count: validationSummary.mismatched }) }}
                      </v-alert>
                      
                      <v-alert
                        v-if="validationSummary && validationSummary.mismatched === 0 && validationSummary.matched > 0"
                        type="success"
                        dense
                        outlined
                        class="mb-4"
                      >
                        <v-icon left>mdi-check-circle</v-icon>
                        {{ $t('reservations_validator_all_match', { count: validationSummary.matched }) }}
                      </v-alert>

                      <!-- Taula de resultats per data -->
                      <v-divider class="my-4"></v-divider>
                      
                      <div class="text-caption grey--text mb-2">
                        <v-icon x-small>mdi-information</v-icon>
                        {{ $t('reservations_validator_click_to_see_details') }}
                      </div>
                      
                      <div style="max-height: 600px; overflow-y: auto;" class="elevation-1">
                        <v-simple-table dense>
                          <template v-slot:default>
                            <thead>
                              <tr>
                                <th class="text-left" style="position: sticky; top: 0; background: white; z-index: 1;">
                                  <strong>{{ $t('reservations_validator_date') }}</strong>
                                </th>
                                <th class="text-center" style="position: sticky; top: 0; background: white; z-index: 1;">
                                  <strong>{{ $t('reservations_validator_total_reservations') }}</strong>
                                  <div v-if="validationFileData" class="text-caption font-weight-regular">
                                    {{ $t('reservations_validator_calculated_excel') }}
                                  </div>
                                </th>
                                <th 
                                  v-for="header in statusHeaders" 
                                  :key="header" 
                                  class="text-center"
                                  style="position: sticky; top: 0; background: white; z-index: 1;"
                                >
                                  <strong>{{ header }}</strong>
                                  <div v-if="validationFileData" class="text-caption font-weight-regular">
                                    {{ $t('reservations_validator_calculated_excel') }}
                                  </div>
                                </th>
                                <th 
                                  v-if="validationFileData" 
                                  class="text-center"
                                  style="position: sticky; top: 0; background: white; z-index: 1;"
                                >
                                  <strong>{{ $t('reservations_validator_validation') }}</strong>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr 
                                v-for="result in validationResults" 
                                :key="result.date"
                                :class="getRowClass(result)"
                                @click="showDateDetails(result)"
                                style="cursor: pointer;"
                                class="hoverable-row"
                              >
                                <td>
                                  <strong>{{ result.friendlyDate }}</strong>
                                  <v-icon small class="ml-1">mdi-eye</v-icon>
                                </td>
                                <td class="text-center">
                                  <div>
                                    <v-chip 
                                      :color="result.validation && result.validation.totalMatch === false ? 'error' : 'primary'" 
                                      small
                                    >
                                      {{ result.total }}
                                    </v-chip>
                                    <div v-if="result.validation && result.validation.expectedTotal !== undefined" class="text-caption">
                                      {{ result.validation.expectedTotal }}
                                    </div>
                                  </div>
                                </td>
                                <td 
                                  v-for="status in statusHeaders" 
                                  :key="status" 
                                  class="text-center"
                                >
                                  <div v-if="result[status] !== undefined || (result.validation && result.validation[status] !== undefined)">
                                    <v-chip 
                                      :color="getStatusChipColor(status, result, status)" 
                                      small
                                      v-if="result[status] !== undefined"
                                    >
                                      {{ result[status] || 0 }}
                                    </v-chip>
                                    <span v-else class="grey--text">0</span>
                                    <div v-if="result.validation && result.validation[status] !== undefined" class="text-caption grey--text">
                                      {{ result.validation[status] }}
                                    </div>
                                  </div>
                                  <span v-else class="grey--text">-</span>
                                </td>
                                <td v-if="validationFileData" class="text-center">
                                  <v-icon 
                                    v-if="result.validation"
                                    :color="result.validation.allMatch ? 'success' : 'error'"
                                    small
                                  >
                                    {{ result.validation.allMatch ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                                  </v-icon>
                                  <v-icon v-else color="grey" small>
                                    mdi-minus-circle
                                  </v-icon>
                                </td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </div>
                      
                      <!-- Detalls addicionals -->
                      <v-divider class="my-4"></v-divider>
                      
                      <v-simple-table dense>
                        <template v-slot:default>
                          <tbody>
                            <tr>
                              <td><strong>{{ $t('reservations_validator_total_in_file') }}</strong></td>
                              <td>{{ fileData ? fileData.length : 0 }}</td>
                            </tr>
                            <tr>
                              <td><strong>{{ $t('reservations_validator_checkin_column_label') }}</strong></td>
                              <td>{{ checkInColumn }}</td>
                            </tr>
                            <tr>
                              <td><strong>{{ $t('reservations_validator_checkout_column_label') }}</strong></td>
                              <td>{{ checkOutColumn }}</td>
                            </tr>
                            <tr v-if="statusColumn">
                              <td><strong>{{ $t('reservations_validator_status_column_label') }}</strong></td>
                              <td>{{ statusColumn }}</td>
                            </tr>
                            <tr>
                              <td><strong>{{ $t('reservations_validator_date_format_label') }}</strong></td>
                              <td>{{ dateFormat }}</td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>

                      <!-- Errors de parsing -->
                      <v-alert
                        v-if="parsingErrors.length > 0"
                        type="warning"
                        dense
                        outlined
                        class="mt-4"
                      >
                        <div class="font-weight-bold mb-2">
                          {{ $t('reservations_validator_errors_detected', { count: parsingErrors.length }) }}
                        </div>
                        <div class="text-caption" style="max-height: 200px; overflow-y: auto;">
                          <div v-for="(error, idx) in parsingErrors.slice(0, 10)" :key="idx">
                            {{ $t('reservations_validator_row_error', { row: error.row, message: error.message }) }}
                          </div>
                          <div v-if="parsingErrors.length > 10" class="mt-2">
                            {{ $t('reservations_validator_more_errors', { count: parsingErrors.length - 10 }) }}
                          </div>
                        </div>
                      </v-alert>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Mostra de dades (primeres 5 files) -->
                <v-col cols="12" v-if="fileData && fileData.length > 0">
                  <v-expansion-panels>
                    <v-expansion-panel>
                      <v-expansion-panel-header>
                        <div>
                          <v-icon left>mdi-table-eye</v-icon>
                          {{ $t('reservations_validator_preview') }}
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-simple-table dense>
                          <template v-slot:default>
                            <thead>
                              <tr>
                                <th v-for="header in headers" :key="header">{{ header }}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(row, idx) in fileData.slice(0, 5)" :key="idx">
                                <td v-for="header in headers" :key="header">
                                  {{ row[header] }}
                                </td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Card amb configuració actual del hotel -->
      <v-row v-if="current_hotel && current_hotel.settings">
        <v-col cols="12">
          <v-card outlined>
            <v-card-title>
              <v-icon left>mdi-cog</v-icon>
              {{ $t('reservations_validator_hotel_config') }}
            </v-card-title>
            <v-card-text>
              <v-simple-table dense>
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td><strong>{{ $t('reservations_validator_config_date_format') }}</strong></td>
                      <td>{{ detectedDateFormat || $t('reservations_validator_not_configured') }}</td>
                    </tr>
                    <tr v-if="detectedColumns.checkIn || detectedColumns.checkOut || detectedColumns.status">
                      <td><strong>{{ $t('reservations_validator_configured_columns') }}</strong></td>
                      <td>
                        {{ $t('reservations_validator_checkin_label', { value: detectedColumns.checkIn || $t('reservations_validator_not_detected') }) }}<br>
                        {{ $t('reservations_validator_checkout_label', { value: detectedColumns.checkOut || $t('reservations_validator_not_detected') }) }}<br>
                        {{ $t('reservations_validator_status_label', { value: detectedColumns.status || $t('reservations_validator_not_detected') }) }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Diàleg de detall de reserves -->
    <v-dialog v-model="detailDialog" max-width="900">
      <v-card v-if="selectedDateDetails">
        <v-card-title class="text-h5 pb-2">
          <v-icon left>mdi-calendar-search</v-icon>
          {{ $t('reservations_validator_detail_title', { date: selectedDateDetails.friendlyDate }) }}
          <v-spacer></v-spacer>
          <v-btn icon @click="detailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-subtitle class="pt-4 pb-3">
          <v-chip color="primary" small>
            {{ $t('reservations_validator_active_reservations', { count: selectedDateDetails.reservations.length }) }}
          </v-chip>
          <span v-if="statusColumn && selectedDateDetails.Confirmada !== undefined" class="ml-2">
            <v-chip color="success" x-small>
              {{ $t('reservations_validator_confirmed', { count: selectedDateDetails.Confirmada || 0 }) }}
            </v-chip>
          </span>
          <span v-if="statusColumn && selectedDateDetails.Cancelada !== undefined" class="ml-1">
            <v-chip color="orange" x-small>
              {{ $t('reservations_validator_cancelled', { count: selectedDateDetails.Cancelada || 0 }) }}
            </v-chip>
          </span>
        </v-card-subtitle>
        
        <v-card-text style="max-height: 500px; overflow-y: auto;" class="pt-2 pb-2">
          <v-expansion-panels accordion flat>
            <v-expansion-panel
              v-for="(reservation, idx) in selectedDateDetails.reservations"
              :key="idx"
            >
              <v-expansion-panel-header class="py-1 min-height-auto">
                <div class="d-flex align-center">
                  <div class="mr-3">
                    <v-chip 
                      v-if="statusColumn"
                      :color="getStatusColor(reservation.status)" 
                      x-small
                    >
                      {{ reservation.status }}
                    </v-chip>
                  </div>
                  <div class="flex-grow-1">
                    <div>
                      <v-icon small class="mr-1">mdi-identifier</v-icon>
                      <strong class="mr-3">{{ reservation.reservationNumber }}</strong>
                      <span>
                        <strong>{{ formatDate(reservation.checkIn) }}</strong>
                        <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                        <strong>{{ formatDate(reservation.checkOut) }}</strong>
                                        <span class="grey--text text--darken-1 ml-1">
                          ({{ $t('reservations_validator_nights', { count: getDaysDiff(reservation.checkIn, reservation.checkOut) }) }})
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="py-0">
                <v-simple-table dense>
                  <template v-slot:default>
                    <tbody>
                      <tr v-for="(value, key) in reservation.otherData" :key="key">
                        <td class="text-right" style="width: 40%;"><strong>{{ key }}</strong></td>
                        <td>{{ value }}</td>
                      </tr>
                      <tr v-if="Object.keys(reservation.otherData).length === 0">
                        <td colspan="2" class="text-center grey--text">
                          {{ $t('reservations_validator_no_additional_data') }}
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="detailDialog = false">
            {{ $t('reservations_validator_close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </a-page>
</template>

<script>
import aPage from "@/components/aPage";
import { mapGetters } from "vuex";
import * as XLSX from "xlsx";
import Vue from "vue";
import * as Utils from "@/utils";

export default {
  name: "reservations-validator",
  components: {
    aPage
  },
  data: () => ({
    app_id: "reservations-validator-app",
    image_source: "/img/header-2.jpg",
    image_height: 240,
    
    // File handling
    file: null,
    fileData: null,
    headers: [],
    loadedFromServer: false,
    loadingFromServer: false,
    
    // Validation file
    validationFile: null,
    validationFileData: null,
    validationHeaders: [],
    
    // Column selection
    checkInColumn: null,
    checkOutColumn: null,
    statusColumn: null,
    
    // Date handling
    dateFormat: "DD/MM/YYYY",
    validationDateStart: new Date().toISOString().split("T")[0],
    validationDateEnd: new Date().toISOString().split("T")[0],
    datePickerMenuStart: false,
    datePickerMenuEnd: false,
    minCheckInDate: null,
    maxCheckOutDate: null,
    
    // Validation
    validating: false,
    validationResults: [],
    parsingErrors: [],
    
    // Detail dialog
    detailDialog: false,
    selectedDateDetails: null,
    
    // Detection
    detectedDateFormat: "",
    detectedColumns: {
      checkIn: null,
      checkOut: null,
      status: null
    }
  }),
  computed: {
    canValidate() {
      return (
        this.fileData &&
        this.checkInColumn &&
        this.checkOutColumn &&
        this.dateFormat &&
        this.validationDateStart &&
        this.validationDateEnd
      );
    },
    friendlyValidationDateStart() {
      return this.validationDateStart
        ? Utils.getFriendlyDate(this.validationDateStart, true)
        : "";
    },
    friendlyValidationDateEnd() {
      return this.validationDateEnd
        ? Utils.getFriendlyDate(this.validationDateEnd, true)
        : "";
    },
    statusHeaders() {
      // Extreure tots els estats únics trobats
      if (!this.statusColumn || this.validationResults.length === 0) {
        return [];
      }
      
      const allStatuses = new Set();
      this.validationResults.forEach(result => {
        Object.keys(result).forEach(key => {
          // Filtrar claus que no són estats
          if (
            key !== "date" && 
            key !== "total" && 
            key !== "friendlyDate" && 
            key !== "reservations" && 
            key !== "validation"
          ) {
            allStatuses.add(key);
          }
        });
      });
      
      return Array.from(allStatuses).sort();
    },
    totalStats() {
      if (this.validationResults.length === 0) return null;
      
      const totals = this.validationResults.map(r => r.total);
      const sum = totals.reduce((acc, val) => acc + val, 0);
      const max = Math.max(...totals);
      const min = Math.min(...totals);
      const average = (sum / totals.length).toFixed(1);
      
      return { average, max, min, sum };
    },
    dateRangeWarning() {
      if (!this.validationDateStart || !this.validationDateEnd) return null;
      
      const start = Vue.moment(this.validationDateStart, "YYYY-MM-DD");
      const end = Vue.moment(this.validationDateEnd, "YYYY-MM-DD");
      const days = end.diff(start, "days") + 1;
      
      if (days > 365) {
        return this.$t('reservations_validator_warning_365_days', { days });
      } else if (days > 90) {
        return this.$t('reservations_validator_warning_90_days', { days });
      }
      
      return null;
    },
    validationSummary() {
      if (!this.validationFileData || !this.validationResults.length) return null;
      
      let datesValidated = 0;
      let datesMatched = 0;
      let datesMismatched = 0;
      
      this.validationResults.forEach(result => {
        if (result.validation) {
          datesValidated++;
          if (result.validation.allMatch) {
            datesMatched++;
          } else {
            datesMismatched++;
          }
        }
      });
      
      return {
        total: datesValidated,
        matched: datesMatched,
        mismatched: datesMismatched
      };
    },
    ...mapGetters({
      current_hotel: "current_hotel",
      current_user: "current_user"
    })
  },
  methods: {
    handleFileUpload(file) {
      if (!file) {
        this.resetData();
        return;
      }

      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          this.processFileData(data);
        } catch (error) {
          // console.error("Error processant l'arxiu:", error);
          Utils.showError(this, "Error processant l'arxiu: " + error.message);
          this.resetData();
        }
      };
      
      reader.readAsArrayBuffer(file);
    },
    
    processFileData(data) {
      try {
        const workbook = XLSX.read(data, { type: "array" });
        
        // Llegir el primer full
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        
        if (jsonData.length === 0) {
          Utils.showError(this, "El fitxer està buit o no té dades");
          this.resetData();
          return;
        }
        
        // Guardar les dades
        this.fileData = jsonData;
        
        // Extreure capçaleres
        this.headers = Object.keys(jsonData[0]);
        
        // Intentar detectar les columnes automàticament
        this.autoDetectColumns();
        
        // Calcular rang de dates després de detectar columnes
        this.calculateDateRange();
        
      } catch (error) {
        // console.error("Error processant l'arxiu:", error);
        Utils.showError(this, "Error processant l'arxiu: " + error.message);
        this.resetData();
      }
    },
    
    async loadFileFromServer(import_task_id) {
      this.loadingFromServer = true;
      
      try {
        const blob = await this.$store.dispatch("getImportFileContents", {
          hotel_id: this.current_hotel.id,
          import_task_id: import_task_id
        });
        
        if (!blob) {
          Utils.showError(this, "No s'ha pogut carregar el fitxer des del servidor");
          this.$router.push({ name: "revenue-import" });
          return;
        }
        
        // El servidor retorna un Blob, convertir-lo a ArrayBuffer
        const arrayBuffer = await blob.arrayBuffer();
        const arrayData = new Uint8Array(arrayBuffer);
        
        // Processar el fitxer
        this.processFileData(arrayData);
        this.loadedFromServer = true;
        
        Utils.showSuccess(this, "Fitxer carregat des del servidor correctament");
        
      } catch (error) {
        // console.error("Error carregant el fitxer des del servidor:", error);
        Utils.showError(this, "Error carregant el fitxer: " + error.message);
        this.$router.push({ name: "revenue-import" });
      } finally {
        this.loadingFromServer = false;
      }
    },
    
    handleValidationFileUpload(file) {
      if (!file) {
        this.validationFileData = null;
        this.validationHeaders = [];
        this.compareWithValidationFile();
        return;
      }

      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          
          // Llegir el primer full
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);
          
          if (jsonData.length === 0) {
            Utils.showError(this, "El fitxer de validació està buit");
            return;
          }
          
          // Guardar les dades
          this.validationFileData = jsonData;
          this.validationHeaders = Object.keys(jsonData[0]);
          
          // Comparar amb els resultats actuals
          this.compareWithValidationFile();
          
          Utils.showSuccess(this, "Arxiu de validació carregat correctament");
          
        } catch (error) {
          // console.error("Error processant l'arxiu de validació:", error);
          Utils.showError(this, "Error processant l'arxiu de validació: " + error.message);
        }
      };
      
      reader.readAsArrayBuffer(file);
    },
    
    calculateDateRange() {
      if (!this.fileData || !this.checkInColumn || !this.checkOutColumn) {
        return;
      }
      
      let minDate = null;
      let maxDate = null;
      
      this.fileData.forEach(row => {
        try {
          const checkInValue = row[this.checkInColumn];
          const checkOutValue = row[this.checkOutColumn];
          
          if (!checkInValue || !checkOutValue) return;
          
          const checkInMoment = this.parseDate(checkInValue);
          const checkOutMoment = this.parseDate(checkOutValue);
          
          if (!checkInMoment || !checkInMoment.isValid()) return;
          if (!checkOutMoment || !checkOutMoment.isValid()) return;
          
          // Actualitzar mínima check-in
          if (!minDate || checkInMoment.isBefore(minDate)) {
            minDate = checkInMoment;
          }
          
          // Actualitzar màxima check-out
          if (!maxDate || checkOutMoment.isAfter(maxDate)) {
            maxDate = checkOutMoment;
          }
        } catch (error) {
          // Ignorar errors individuals
        }
      });
      
      if (minDate && maxDate) {
        this.minCheckInDate = minDate.format("YYYY-MM-DD");
        this.maxCheckOutDate = maxDate.format("YYYY-MM-DD");
        
        // Proposar aquestes dates als selectors
        this.validationDateStart = this.minCheckInDate;
        this.validationDateEnd = this.maxCheckOutDate;
      }
    },
    
    autoDetectColumns() {
      // Detectar format de data des de settings
      if (this.current_hotel && this.current_hotel.settings) {
        if (this.current_hotel.settings.manual_revenue_date_format) {
          this.detectedDateFormat = this.current_hotel.settings.manual_revenue_date_format;
          this.dateFormat = this.current_hotel.settings.manual_revenue_date_format;
        }
        
        // Detectar columnes des de la configuració
        if (this.current_hotel.settings.manual_revenue_reservation_cols_config) {
          const cols = this.current_hotel.settings.manual_revenue_reservation_cols_config;
          
          // Buscar stay.start_date i stay.end_date
          const checkInIndex = cols.indexOf("stay.start_date");
          const checkOutIndex = cols.indexOf("stay.end_date");
          const statusIndex = cols.indexOf("status.code");
          
          if (checkInIndex !== -1 && checkInIndex < this.headers.length) {
            this.checkInColumn = this.headers[checkInIndex];
            this.detectedColumns.checkIn = this.headers[checkInIndex];
          }
          
          if (checkOutIndex !== -1 && checkOutIndex < this.headers.length) {
            this.checkOutColumn = this.headers[checkOutIndex];
            this.detectedColumns.checkOut = this.headers[checkOutIndex];
          }
          
          if (statusIndex !== -1 && statusIndex < this.headers.length) {
            this.statusColumn = this.headers[statusIndex];
            this.detectedColumns.status = this.headers[statusIndex];
          }
        }
      }
      
      // Si no s'han detectat, intentar cercar per noms comuns
      if (!this.checkInColumn) {
        const checkInNames = [
          "check-in", "checkin", "check_in", "arrival", "entrada", 
          "fecha entrada", "data entrada", "start_date", "start date"
        ];
        
        for (const header of this.headers) {
          if (checkInNames.some(name => header.toLowerCase().includes(name))) {
            this.checkInColumn = header;
            break;
          }
        }
      }
      
      if (!this.checkOutColumn) {
        const checkOutNames = [
          "check-out", "checkout", "check_out", "departure", "sortida", 
          "fecha salida", "data sortida", "end_date", "end date"
        ];
        
        for (const header of this.headers) {
          if (checkOutNames.some(name => header.toLowerCase().includes(name))) {
            this.checkOutColumn = header;
            break;
          }
        }
      }
      
      if (!this.statusColumn) {
        const statusNames = [
          "estado", "status", "estat", "state"
        ];
        
        for (const header of this.headers) {
          if (statusNames.some(name => header.toLowerCase().includes(name))) {
            this.statusColumn = header;
            break;
          }
        }
      }
    },
    
    validateReservations() {
      if (!this.canValidate) return;
      
      this.validating = true;
      this.parsingErrors = [];
      this.validationResults = [];
      
      try {
        const startMoment = Vue.moment(this.validationDateStart, "YYYY-MM-DD");
        const endMoment = Vue.moment(this.validationDateEnd, "YYYY-MM-DD");
        
        // Validar que la data inicial no sigui posterior a la final
        if (startMoment.isAfter(endMoment)) {
          Utils.showError(this, "La data inicial no pot ser posterior a la data final");
          this.validating = false;
          return;
        }
        
        // Crear un objecte per emmagatzemar els resultats per data
        const resultsByDate = {};
        
        // Inicialitzar totes les dates del rang
        let currentDate = startMoment.clone();
        while (currentDate.isSameOrBefore(endMoment, "day")) {
          const dateKey = currentDate.format("YYYY-MM-DD");
          resultsByDate[dateKey] = {
            date: dateKey,
            friendlyDate: Utils.getFriendlyDate(dateKey, true),
            total: 0,
            reservations: [] // Guardar les reserves originals
          };
          currentDate.add(1, "day");
        }
        
        // Iterar sobre totes les reserves i comptar per cada data
        this.fileData.forEach((row, index) => {
          try {
            const checkInValue = row[this.checkInColumn];
            const checkOutValue = row[this.checkOutColumn];
            const statusValue = this.statusColumn ? row[this.statusColumn] : null;
            
            if (!checkInValue || !checkOutValue) {
              this.parsingErrors.push({
                row: index + 2, // +2 perquè compte la capçalera i l'index comença a 0
                message: "Falta data d'entrada o sortida"
              });
              return;
            }
            
            // Intentar parsejar les dates
            const checkInMoment = this.parseDate(checkInValue);
            const checkOutMoment = this.parseDate(checkOutValue);
            
            if (!checkInMoment || !checkInMoment.isValid()) {
              this.parsingErrors.push({
                row: index + 2,
                message: `No es pot parsejar la data d'entrada: ${checkInValue}`
              });
              return;
            }
            
            if (!checkOutMoment || !checkOutMoment.isValid()) {
              this.parsingErrors.push({
                row: index + 2,
                message: `No es pot parsejar la data de sortida: ${checkOutValue}`
              });
              return;
            }
            
            // Per cada data del rang, comprovar si la reserva està activa
            currentDate = startMoment.clone();
            while (currentDate.isSameOrBefore(endMoment, "day")) {
            // Validar: la reserva està activa si:
              // checkIn <= currentDate < checkOut
              if (
                (checkInMoment.isSameOrBefore(currentDate, "day")) &&
                (checkOutMoment.isAfter(currentDate, "day"))
              ) {
                const dateKey = currentDate.format("YYYY-MM-DD");
                resultsByDate[dateKey].total++;
                
                // Comptar per estat si la columna d'estat existeix
                if (statusValue) {
                  const status = String(statusValue).trim();
                  if (!resultsByDate[dateKey][status]) {
                    resultsByDate[dateKey][status] = 0;
                  }
                  resultsByDate[dateKey][status]++;
                }
                
                // Guardar la informació de la reserva
                const reservationDetail = {
                  checkIn: checkInMoment.format("YYYY-MM-DD"),
                  checkOut: checkOutMoment.format("YYYY-MM-DD"),
                  status: statusValue ? String(statusValue).trim() : "-",
                  reservationNumber: row["Nº de reserva"] || row["codi reservation_pms_id"] || row["reservation_pms_id"] || "-",
                  otherData: {}
                };
                
                // Afegir altres camps del row (excepte check-in, check-out, status i número de reserva)
                Object.keys(row).forEach(key => {
                  if (
                    key !== this.checkInColumn && 
                    key !== this.checkOutColumn && 
                    key !== this.statusColumn &&
                    key !== "Nº de reserva" &&
                    key !== "codi reservation_pms_id" &&
                    key !== "reservation_pms_id" &&
                    row[key] !== null &&
                    row[key] !== undefined &&
                    row[key] !== ""
                  ) {
                    reservationDetail.otherData[key] = row[key];
                  }
                });
                
                resultsByDate[dateKey].reservations.push(reservationDetail);
              }
              
              currentDate.add(1, "day");
            }
            
          } catch (error) {
            this.parsingErrors.push({
              row: index + 2,
              message: `Error processant la fila: ${error.message}`
            });
          }
        });
        
        // Convertir l'objecte a array i ordenar per data
        this.validationResults = Object.values(resultsByDate).sort((a, b) => {
          return a.date.localeCompare(b.date);
        });
        
        // Si hi ha un fitxer de validació carregat, comparar automàticament
        if (this.validationFileData) {
          this.compareWithValidationFile();
        }
        
      } catch (error) {
        // console.error("Error validant reserves:", error);
        Utils.showError(this, "Error validant reserves: " + error.message);
      } finally {
        this.validating = false;
      }
    },
    
    parseDate(dateValue) {
      // Intentar parsejar amb diferents formats
      let parsedDate = null;
      
      // Primer intentar amb el format especificat
      parsedDate = Vue.moment(dateValue, this.dateFormat, true);
      if (parsedDate.isValid()) return parsedDate;
      
      // Si és un número (Excel serial date)
      if (typeof dateValue === "number") {
        // Excel dates comencen des de 1900-01-01
        const excelEpoch = new Date(1899, 11, 30);
        const date = new Date(excelEpoch.getTime() + dateValue * 86400000);
        return Vue.moment(date);
      }
      
      // Intentar amb formats comuns
      const formats = [
        "DD/MM/YYYY",
        "YYYY-MM-DD",
        "MM/DD/YYYY",
        "DD-MM-YYYY",
        "YYYY/MM/DD",
        "D/M/YYYY",
        "D-M-YYYY"
      ];
      
      for (const format of formats) {
        parsedDate = Vue.moment(dateValue, format, true);
        if (parsedDate.isValid()) return parsedDate;
      }
      
      // Últim intent: deixar que moment.js ho intenti automàticament
      parsedDate = Vue.moment(dateValue);
      return parsedDate.isValid() ? parsedDate : null;
    },
    
    compareWithValidationFile() {
      if (!this.validationFileData || !this.validationResults.length) {
        // Netejar validacions si no hi ha arxiu
        this.validationResults.forEach(result => {
          delete result.validation;
        });
        return;
      }
      
      // Crear un mapa de les dades de validació per data
      const validationMap = {};
      
      this.validationFileData.forEach(row => {
        // Buscar la columna de data (diverses variacions)
        const dateValue = row["Fecha"] || row["Date"] || row["fecha"] || row["DATA"] || row["Data"];
        if (!dateValue) return;
        
        // Parsejar la data
        const dateMoment = this.parseDate(dateValue);
        if (!dateMoment || !dateMoment.isValid()) return;
        
        const dateKey = dateMoment.format("YYYY-MM-DD");
        
        // Extreure les dades (amb diverses variacions de noms)
        const nochesTotales = Number(row["Noches totales"] || row["noches totales"] || row["Total"] || 0);
        const bloqueos = Number(row["Bloqueos"] || row["bloqueos"] || row["Bloqueos"] || 0);
        const confirmadas = Number(row["Nº noches confirmadas"] || row["noches confirmadas"] || row["Confirmadas"] || 0);
        const canceladas = Number(row["Nº noches canceladas"] || row["noches canceladas"] || row["Canceladas"] || 0);
        
        // El total esperat és Noches totales - Bloqueos
        const expectedTotal = nochesTotales - bloqueos;
        
        validationMap[dateKey] = {
          expectedTotal: expectedTotal,
          Confirmada: confirmadas,
          Cancelada: canceladas
        };
      });
      
      // Comparar amb els resultats
      this.validationResults.forEach(result => {
        const validation = validationMap[result.date];
        
        if (validation) {
          result.validation = {
            expectedTotal: validation.expectedTotal,
            totalMatch: result.total === validation.expectedTotal,
            allMatch: true
          };
          
          // Comparar cada estat
          this.statusHeaders.forEach(status => {
            if (validation[status] !== undefined) {
              result.validation[status] = validation[status];
              const match = (result[status] || 0) === validation[status];
              if (!match) {
                result.validation.allMatch = false;
              }
            }
          });
          
          // Comprovar el total també afecta allMatch
          if (!result.validation.totalMatch) {
            result.validation.allMatch = false;
          }
        } else {
          result.validation = null;
        }
      });
    },
    
    getStatusColor(status) {
      // Mapa de colors per diferents estats
      const statusColors = {
        "Confirmada": "success",
        "Confirmed": "success",
        "Cancelada": "orange",
        "Cancelled": "orange",
        "Canceled": "orange",
        "Pendiente": "warning",
        "Pending": "warning",
        "No Show": "grey darken-1",
        "NoShow": "grey darken-1"
      };
      
      return statusColors[status] || "info";
    },
    
    getStatusChipColor(status, result, statusKey) {
      // Si hi ha validació i no coincideix, mostrar en vermell
      if (result.validation && result.validation[statusKey] !== undefined) {
        const match = (result[statusKey] || 0) === result.validation[statusKey];
        if (!match) {
          return "error";
        }
      }
      
      // Sinó, usar el color normal de l'estat
      return this.getStatusColor(status);
    },
    
    getRowClass(result) {
      if (result.validation && !result.validation.allMatch) {
        return "red lighten-5";
      }
      return "";
    },
    
    clearValidationFile() {
      this.validationFile = null;
      this.validationFileData = null;
      this.validationHeaders = [];
      this.compareWithValidationFile();
    },
    
    showDateDetails(result) {
      this.selectedDateDetails = result;
      this.detailDialog = true;
    },
    
    formatDate(dateString) {
      return Utils.getFriendlyDate(dateString, true);
    },
    
    getDaysDiff(checkIn, checkOut) {
      const start = Vue.moment(checkIn, "YYYY-MM-DD");
      const end = Vue.moment(checkOut, "YYYY-MM-DD");
      return end.diff(start, "days");
    },
    
    resetData() {
      this.fileData = null;
      this.headers = [];
      this.checkInColumn = null;
      this.checkOutColumn = null;
      this.statusColumn = null;
      this.validationResults = [];
      this.parsingErrors = [];
      this.validationFile = null;
      this.validationFileData = null;
      this.validationHeaders = [];
      this.minCheckInDate = null;
      this.maxCheckOutDate = null;
      this.loadedFromServer = false;
    }
  },
  watch: {
    checkInColumn() {
      // Recalcular rang de dates quan canvia la columna de check-in
      if (this.fileData && this.checkInColumn && this.checkOutColumn) {
        this.calculateDateRange();
      }
    },
    checkOutColumn() {
      // Recalcular rang de dates quan canvia la columna de check-out
      if (this.fileData && this.checkInColumn && this.checkOutColumn) {
        this.calculateDateRange();
      }
    }
  },
  mounted() {
    // Inicialitzar amb la configuració del hotel si està disponible
    if (this.current_hotel && this.current_hotel.settings) {
      if (this.current_hotel.settings.manual_revenue_date_format) {
        this.detectedDateFormat = this.current_hotel.settings.manual_revenue_date_format;
        this.dateFormat = this.current_hotel.settings.manual_revenue_date_format;
      }
    }
    
    // Si hi ha un import_task_id als query params de la ruta, carregar el fitxer automàticament
    if (this.$route.query.import_task_id) {
      this.loadFileFromServer(this.$route.query.import_task_id);
    }
  }
};
</script>

<style scoped>
/* Assegurar que les capçaleres sticky tinguin el fons correcte */
.theme--light.v-data-table thead th {
  background: white;
}

.theme--dark.v-data-table thead th {
  background: #1e1e1e;
}

/* Files amb errors de validació */
.red.lighten-5 {
  background-color: #ffebee !important;
}

.theme--dark .red.lighten-5 {
  background-color: rgba(255, 82, 82, 0.1) !important;
}

/* Hover en files clicables */
.hoverable-row:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

.theme--dark .hoverable-row:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

/* Mantenir el vermell en files amb error però més fosc en hover */
.red.lighten-5.hoverable-row:hover {
  background-color: #ffcdd2 !important;
}

.theme--dark .red.lighten-5.hoverable-row:hover {
  background-color: rgba(255, 82, 82, 0.15) !important;
}

/* Espaiat entre expansion panels */
.v-expansion-panel + .v-expansion-panel {
  margin-top: 2px !important;
}

/* Reduir altura mínima dels expansion panel headers */
.v-expansion-panel-header.min-height-auto {
  min-height: 30px !important;
}

/* Reduir padding del content dels expansion panels */
.v-expansion-panel-content__wrap {
  padding: 8px 16px 8px 16px !important;
}
</style>

